import { useCallback, useEffect, useRef, useState } from 'react';

import type { Batch, Order, Shop } from '@notify/shared';
import { OrderStatus, RushMode } from '@notify/shared';

import type { ShopStats } from '../../services/shop.service';
import { shopService } from '../../services/shop.service';

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastItem {
  id: number;
  type: 'success' | 'error' | 'info';
  message: string;
}

function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const show = useCallback((type: ToastItem['type'], message: string) => {
    const id = Date.now();
    setToasts((p) => [...p, { id, type, message }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

function ToastStack({ toasts }: { toasts: ToastItem[] }) {
  if (!toasts.length) return null;
  const bg: Record<ToastItem['type'], string> = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    info: 'bg-[#6366F1]',
  };
  return (
    <div className="fixed bottom-20 right-4 z-[60] flex flex-col gap-2">
      {toasts.map((t) => (
        <div key={t.id} className={`rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg ${bg[t.type]}`}>
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ─── Countdown timer ──────────────────────────────────────────────────────────

function Countdown({ deadline, status }: { deadline: Date | null; status: OrderStatus }) {
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!deadline || (status !== OrderStatus.PENDING && status !== OrderStatus.PREPARING)) return;
    const tick = () => setRemaining(new Date(deadline).getTime() - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline, status]);

  if (status !== OrderStatus.PENDING && status !== OrderStatus.PREPARING) return null;
  if (!deadline) return null;

  if (remaining !== null && remaining <= 0) {
    return <span className="text-xs font-semibold text-red-500">Overdue</span>;
  }

  if (remaining === null) return null;

  const totalSec = Math.floor(remaining / 1000);
  const mins = Math.floor(totalSec / 60).toString().padStart(2, '0');
  const secs = (totalSec % 60).toString().padStart(2, '0');

  return (
    <span className={`font-mono text-xs font-semibold ${remaining < 60000 ? 'text-red-500' : 'text-[#64748B]'}`}>
      {mins}:{secs}
    </span>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: OrderStatus }) {
  const styles: Record<OrderStatus, string> = {
    [OrderStatus.PENDING]: 'bg-gray-100 text-gray-600',
    [OrderStatus.PREPARING]: 'bg-amber-100 text-amber-700',
    [OrderStatus.READY]: 'bg-green-100 text-green-700',
    [OrderStatus.COLLECTED]: 'bg-blue-100 text-blue-700',
    [OrderStatus.CANCELLED]: 'bg-red-100 text-red-500',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${styles[status]}`}>
      {status}
    </span>
  );
}

// ─── Rush mode panel ──────────────────────────────────────────────────────────

const RUSH_OPTIONS: { mode: RushMode; label: string; style: string; activeStyle: string }[] = [
  {
    mode: RushMode.NORMAL,
    label: 'Normal',
    style: 'border-gray-200 text-gray-600 hover:bg-gray-50',
    activeStyle: 'bg-gray-600 border-gray-600 text-white',
  },
  {
    mode: RushMode.BUSY,
    label: 'Busy',
    style: 'border-amber-200 text-amber-700 hover:bg-amber-50',
    activeStyle: 'bg-amber-500 border-amber-500 text-white',
  },
  {
    mode: RushMode.HEAVY_RUSH,
    label: 'Heavy Rush',
    style: 'border-red-200 text-red-600 hover:bg-red-50',
    activeStyle: 'bg-red-500 border-red-500 text-white',
  },
];

interface RushPanelProps {
  shop: Shop;
  onShopUpdate: (shop: Shop) => void;
  onToast: (type: ToastItem['type'], msg: string) => void;
}

function RushModePanel({ shop, onShopUpdate, onToast }: RushPanelProps) {
  const [saving, setSaving] = useState(false);
  const [prepOverride, setPrepOverride] = useState<string>(
    shop.prepTimeMinutes.toString(),
  );

  async function handleModeChange(mode: RushMode) {
    if (mode === shop.rushMode) return;
    setSaving(true);
    try {
      const updated = await shopService.updateRushMode(mode);
      onShopUpdate(updated);
      setPrepOverride(updated.prepTimeMinutes.toString());
      onToast('success', `Rush mode set to ${mode}`);
    } catch (err) {
      onToast('error', err instanceof Error ? err.message : 'Failed to update mode');
    } finally {
      setSaving(false);
    }
  }

  async function handlePrepOverride() {
    const val = parseInt(prepOverride, 10);
    if (isNaN(val) || val < 1 || val > 120) return;
    setSaving(true);
    try {
      const updated = await shopService.updateRushMode(shop.rushMode, val);
      onShopUpdate(updated);
      onToast('success', `Prep time set to ${val} min`);
    } catch (err) {
      onToast('error', err instanceof Error ? err.message : 'Failed to update prep time');
    } finally {
      setSaving(false);
    }
  }

  const rushLabel: Record<RushMode, string> = {
    [RushMode.NORMAL]: 'Normal',
    [RushMode.BUSY]: 'Busy',
    [RushMode.HEAVY_RUSH]: 'Heavy Rush',
  };
  const rushColor: Record<RushMode, string> = {
    [RushMode.NORMAL]: 'bg-gray-100 text-gray-700',
    [RushMode.BUSY]: 'bg-amber-100 text-amber-800',
    [RushMode.HEAVY_RUSH]: 'bg-red-100 text-red-700',
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Current Mode</p>
          <span className={`mt-1 inline-flex items-center rounded-full px-3 py-1 text-sm font-bold ${rushColor[shop.rushMode]}`}>
            {rushLabel[shop.rushMode]}
          </span>
        </div>

        <div className="flex gap-2">
          {RUSH_OPTIONS.map(({ mode, label, style, activeStyle }) => (
            <button
              key={mode}
              type="button"
              disabled={saving}
              onClick={() => void handleModeChange(mode)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                shop.rushMode === mode ? activeStyle : style
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-[#64748B]">Prep time:</span>
          <input
            type="number"
            min={1}
            max={120}
            className="w-16 rounded-lg border border-gray-200 px-2 py-1 text-center text-sm font-medium text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
            value={prepOverride}
            onChange={(e) => setPrepOverride(e.target.value)}
            onBlur={() => void handlePrepOverride()}
            onKeyDown={(e) => e.key === 'Enter' && void handlePrepOverride()}
          />
          <span className="text-sm text-[#64748B]">min</span>
        </div>
      </div>
    </div>
  );
}

// ─── Stats row ────────────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">{label}</p>
      <p className={`mt-2 text-3xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function StatSkeleton() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="h-3 w-24 animate-pulse rounded bg-gray-200" />
      <div className="mt-3 h-8 w-12 animate-pulse rounded bg-gray-200" />
    </div>
  );
}

// ─── Order row ────────────────────────────────────────────────────────────────

interface OrderRowProps {
  order: Order;
  subscriberCount: number;
  onStatusChange: (orderId: string, status: OrderStatus) => Promise<void>;
  updating: boolean;
}

function OrderRow({ order, subscriberCount, onStatusChange, updating }: OrderRowProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-100 bg-white px-4 py-3 shadow-sm">
      {/* Reference */}
      <span className="min-w-[4rem] font-mono text-base font-bold text-[#0F172A]">
        {order.orderReference}
      </span>

      {/* Status */}
      <StatusBadge status={order.status} />

      {/* Countdown */}
      <Countdown deadline={order.prepDeadline} status={order.status} />

      {/* Subscriber count */}
      <span className="flex items-center gap-1 text-xs text-[#64748B]">
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 22a2 2 0 001.995-1.85L14 20H10a2 2 0 001.995 1.85L12 22zM12 2a7 7 0 016.996 6.76L19 9v5l2 3v1H3v-1l2-3V9a7 7 0 017-7z" />
        </svg>
        {subscriberCount}
      </span>

      {/* Actions */}
      <div className="ml-auto flex items-center gap-2">
        {order.status === OrderStatus.PENDING && (
          <button
            type="button"
            disabled={updating}
            onClick={() => void onStatusChange(order.id, OrderStatus.PREPARING)}
            className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-50 transition-colors"
          >
            Start Preparing
          </button>
        )}
        {order.status === OrderStatus.PREPARING && (
          <button
            type="button"
            disabled={updating}
            onClick={() => void onStatusChange(order.id, OrderStatus.READY)}
            className="rounded-lg bg-green-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-600 disabled:opacity-50 transition-colors"
          >
            Mark Ready
          </button>
        )}
        {order.status === OrderStatus.READY && (
          <button
            type="button"
            disabled={updating}
            onClick={() => void onStatusChange(order.id, OrderStatus.COLLECTED)}
            className="rounded-lg bg-blue-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-600 disabled:opacity-50 transition-colors"
          >
            Mark Collected
          </button>
        )}
        {order.status !== OrderStatus.CANCELLED && order.status !== OrderStatus.COLLECTED && (
          <button
            type="button"
            disabled={updating}
            onClick={() => void onStatusChange(order.id, OrderStatus.CANCELLED)}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-[#64748B] hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Add Order slide-over ─────────────────────────────────────────────────────

interface AddOrderPanelProps {
  open: boolean;
  onClose: () => void;
  batches: Batch[];
  onCreated: (order: Order) => void;
  onError: (msg: string) => void;
}

function AddOrderPanel({ open, onClose, batches, onCreated, onError }: AddOrderPanelProps) {
  const [ref, setRef] = useState('');
  const [batchId, setBatchId] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refError, setRefError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setRef(''); setBatchId(''); setPrepTime(''); setRefError(''); }
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ref.trim()) { setRefError('Order reference is required'); return; }
    setSubmitting(true);
    try {
      const order = await shopService.createOrder({
        order_reference: ref.trim(),
        batch_id: batchId || undefined,
        prep_time_override: prepTime ? parseInt(prepTime, 10) : undefined,
      });
      onCreated(order);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  }

  const openBatches = batches.filter((b) => b.status === 'open');

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 ${open ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
      />
      <div
        className={`fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col bg-white shadow-2xl transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-[#0F172A]">New Order</h2>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#0F172A]" aria-label="Close">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" />
              <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-1 flex-col overflow-y-auto">
          <div className="flex-1 space-y-5 px-6 py-6">
            <div>
              <label htmlFor="order-ref" className="block text-sm font-medium text-[#0F172A]">
                Order Reference <span className="text-red-400">*</span>
              </label>
              <input
                ref={inputRef}
                id="order-ref"
                type="text"
                maxLength={20}
                placeholder="#042"
                className={`mt-1 block w-full rounded-lg border px-3 py-2 font-mono text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#6366F1] ${refError ? 'border-red-400' : 'border-gray-200'}`}
                value={ref}
                onChange={(e) => { setRef(e.target.value); setRefError(''); }}
              />
              {refError && <p className="mt-1 text-xs text-red-500">{refError}</p>}
            </div>

            <div>
              <label htmlFor="order-batch" className="block text-sm font-medium text-[#0F172A]">
                Batch <span className="text-[#94A3B8] font-normal">(optional)</span>
              </label>
              <select
                id="order-batch"
                className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                value={batchId}
                onChange={(e) => setBatchId(e.target.value)}
              >
                <option value="">— No batch —</option>
                {openBatches.map((b) => (
                  <option key={b.id} value={b.id}>{b.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="order-prep" className="block text-sm font-medium text-[#0F172A]">
                Custom Prep Time <span className="text-[#94A3B8] font-normal">(optional, minutes)</span>
              </label>
              <input
                id="order-prep"
                type="number"
                min={1}
                max={120}
                placeholder="Use shop default"
                className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                value={prepTime}
                onChange={(e) => setPrepTime(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-3 border-t border-gray-200 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-[#64748B] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 disabled:opacity-60 transition-colors"
            >
              {submitting ? 'Adding…' : 'Add Order'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ShopDashboardPage() {
  const [shop, setShop] = useState<Shop | null>(null);
  const [stats, setStats] = useState<ShopStats | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [subscriberCounts, _setSubscriberCounts] = useState<Record<string, number>>({});
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [updatingOrder, setUpdatingOrder] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const { toasts, show: showToast } = useToast();
  const refreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const today = new Date().toISOString().slice(0, 10);

  const fetchOrders = useCallback(async () => {
    try {
      const data = await shopService.getOrders({ date: today });
      setOrders(data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch {
      // silent refresh failures
    }
  }, [today]);

  useEffect(() => {
    void (async () => {
      try {
        const [shopData, statsData, ordersData, batchesData] = await Promise.all([
          shopService.getMyShop(),
          shopService.getShopStats(),
          shopService.getOrders({ date: today }),
          shopService.getBatches(),
        ]);
        setShop(shopData);
        setStats(statsData);
        setOrders(ordersData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
        setBatches(batchesData);
      } finally {
        setLoadingInitial(false);
      }
    })();
  }, [today]);

  // Auto-refresh every 30s
  useEffect(() => {
    refreshRef.current = setInterval(() => void fetchOrders(), 30_000);
    return () => { if (refreshRef.current) clearInterval(refreshRef.current); };
  }, [fetchOrders]);

  async function handleStatusChange(orderId: string, status: OrderStatus) {
    setUpdatingOrder(orderId);
    try {
      const updated = await shopService.updateOrderStatus(orderId, status);
      setOrders((prev) => prev.map((o) => (o.id === orderId ? updated : o)));
      if (status === OrderStatus.READY) showToast('info', '🔔 Notified!');
      else showToast('success', `Order marked ${status}`);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to update order');
    } finally {
      setUpdatingOrder(null);
    }
  }

  function handleOrderCreated(order: Order) {
    setOrders((prev) => [order, ...prev]);
    showToast('success', `Order ${order.orderReference} added`);
  }

  const activeOrders = orders.filter(
    (o) => o.status !== OrderStatus.COLLECTED && o.status !== OrderStatus.CANCELLED,
  );

  return (
    <>
      <div className="space-y-6">
        {/* Rush mode panel */}
        {shop ? (
          <RushModePanel shop={shop} onShopUpdate={setShop} onToast={showToast} />
        ) : (
          <div className="h-20 animate-pulse rounded-xl bg-gray-200" />
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {loadingInitial || !stats ? (
            Array.from({ length: 5 }).map((_, i) => <StatSkeleton key={i} />)
          ) : (
            <>
              <StatCard label="Total Orders" value={stats.total_orders} color="text-[#0F172A]" />
              <StatCard label="Pending" value={stats.pending} color="text-gray-500" />
              <StatCard label="Preparing" value={stats.preparing} color="text-amber-600" />
              <StatCard label="Ready" value={stats.ready} color="text-green-600" />
              <StatCard label="Notified" value={stats.notifications_sent} color="text-[#6366F1]" />
            </>
          )}
        </div>

        {/* Live orders */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-[#0F172A]">
              Live Orders
              <span className="ml-2 text-sm font-normal text-[#64748B]">
                ({activeOrders.length} active)
              </span>
            </h2>
            <span className="text-xs text-[#94A3B8]">Refreshes every 30s</span>
          </div>

          {loadingInitial ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-lg bg-gray-200" />
              ))}
            </div>
          ) : activeOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-14 text-center">
              <svg className="mb-3 h-10 w-10 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" strokeLinecap="round" />
                <rect x="9" y="3" width="6" height="4" rx="1" />
              </svg>
              <p className="text-sm font-medium text-[#0F172A]">No active orders</p>
              <p className="mt-0.5 text-xs text-[#64748B]">Add an order using the + button</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeOrders.map((order) => (
                <OrderRow
                  key={order.id}
                  order={order}
                  subscriberCount={subscriberCounts[order.id] ?? 0}
                  onStatusChange={handleStatusChange}
                  updating={updatingOrder === order.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* FAB */}
      <button
        type="button"
        onClick={() => setPanelOpen(true)}
        className="fixed bottom-6 right-6 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-[#6366F1] text-white shadow-lg hover:bg-indigo-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6366F1] focus-visible:ring-offset-2"
        aria-label="Add order"
      >
        <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <line x1="12" y1="5" x2="12" y2="19" strokeLinecap="round" />
          <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
        </svg>
      </button>

      <AddOrderPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        batches={batches}
        onCreated={handleOrderCreated}
        onError={(msg) => showToast('error', msg)}
      />

      <ToastStack toasts={toasts} />
    </>
  );
}
